import { useEffect, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { getBooks as getNewArrivals, postBooks as postNewArrival, deleteBook as deleteNewArrival, patchBook } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const MainContainer = styled.div`
  max-width: 1100px;
  margin: 10px auto;
  font-family: 'Roboto', 'Segoe UI', Arial, sans-serif;
  padding: 10px;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    margin: 5px auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;
`;

const FormContainer = styled.form`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  
  label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #34495e;
    font-size: 12px;
  }
  
  input, textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    transition: all 0.3s;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
    }
  }
  
  textarea {
    min-height: 80px;
    resize: vertical;
  }
  
  input[type="file"] {
    padding: 6px;
    background-color: #f8f9fa;
    border: 1px dashed #ced4da;
    font-size: 11px;
  }
`;

const FileHelpText = styled.div`
  font-size: 10px;
  margin-top: 4px;
  color: #7f8c8d;
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 10px;
  margin-top: 4px;
  font-weight: 500;
  
  &:before {
    content: '⚠️';
    margin-right: 4px;
  }
`;

const StatusMessage = styled.div`
  margin: 0 0 12px;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  font-size: 11px;
  
  &.success {
    background-color: #d4edda;
    color: #155724;
    border-left: 4px solid var(--green);
  }
  
  &.error {
    background-color: #f8d7da;
    color: #721c24;
    border-left: 4px solid #dc3545;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
`;

const Button = styled.button`
  background-color: ${props => props.red ? '#e74c3c' : props.secondary ? '#7f8c8d' : 'var(--primary-color)'};
  color: white;
  padding: ${props => props.small ? '6px 10px' : '8px 14px'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${props => props.small ? '10px' : '11px'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex: ${props => props.small ? '1' : 'none'};
  min-width: 0;
  
  &:hover {
    background-color: ${props => props.red ? '#c0392b' : props.secondary ? '#6c7a7a' : 'var(--primary-color)'};
    transform: translateY(-1px);
    filter: brightness(0.9);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }
`;

const BooksSection = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;
`;

const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const BookCard = styled.div`
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  border-left: 2px solid var(--primary-color);
  max-width: 100%;
  min-width: 0;
  height: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  }
`;

const BookImageContainer = styled.div`
  position: relative;
  height: 150px;
  overflow: hidden;
  background-color: #f8f9fa;
  flex-shrink: 0;
`;

const BookImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const BookDetails = styled.div`
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const BookTitle = styled.h4`
  color: #2c3e50;
  font-size: 14px;
  margin: 0 0 8px 0;
  font-weight: 600;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 36px;
`;

const BookAuthor = styled.div`
  font-size: 12px;
  color: #34495e;
  margin-bottom: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  font-weight: 500;
`;

const BookYear = styled.span`
  color: #7f8c8d;
  font-size: 11px;
`;

const BookMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 6px 0;
  font-size: 11px;
  flex-wrap: wrap;
  gap: 4px;
`;

const BookGenre = styled.div`
  color: #7f8c8d;
  display: inline-flex;
  align-items: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  
  &:before {
    content: '📚';
    margin-right: 3px;
    flex-shrink: 0;
  }
`;

const BookQuantity = styled.div`
  color: #7f8c8d;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  
  &:before {
    content: '🔢';
    margin-right: 3px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
  flex-wrap: wrap;
`;

const PreviewImage = styled.img`
  max-width: 150px;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
`;

const PreviewLabel = styled.div`
  display: inline-block;
  background: var(--primary-color);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  margin-top: 8px;
  margin-bottom: 4px;
`;

const SpinnerAnimation = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-left: 6px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  ${SpinnerAnimation}
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;
`;

const LoadingIcon = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-left: 6px;
  border: 2px solid #ecf0f1;
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  
  ${SpinnerAnimation}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#34495e'};
  border: ${props => props.active ? 'none' : '1px solid #e0e0e0'};
  padding: 4px 8px;
  margin: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s;
  font-size: 11px;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : '#e9ecef'};
    transform: translateY(-1px);
    filter: ${props => props.active ? 'brightness(0.9)' : 'none'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

function NewArrivals() {
  const [formData, setFormData] = useState({
    title: '',
    quantity: '',
    author: '',
    year: '',
    Genr: '',
    isbn: '',
    annotation: '',
  });

  const [books, setBooks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLocalPreview, setIsLocalPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Добавляем useRef для файлового поля
  const fileInputRef = useRef(null);

  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const arrivalsPerPage = 9;
  const { showNotification } = useNotification();

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const response = await getNewArrivals();
      if (response?.books) {
        setBooks(response.books);
      }
    } catch (error) {
      console.error('Ошибка при получении списка книг:', error);
      showNotification('error', 'Не удалось загрузить список книг');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Проверка названия
    if (!formData.title.trim()) {
      newErrors.title = 'Название книги обязательно';
    } else if (formData.title.length > 150) {
      newErrors.title = 'Название книги не должно превышать 150 символов';
    }

    // Проверка автора
    if (!formData.author.trim()) {
      newErrors.author = 'Автор книги обязателен';
    } else if (formData.author.length > 150) {
      newErrors.author = 'Имя автора не должно превышать 150 символов';
    }

    // Проверка года
    if (!formData.year) {
      newErrors.year = 'Год издания обязателен';
    } else if (formData.year === '') {
      newErrors.year = 'Год издания не может быть пустым';
    } else {
      const yearValue = Number(formData.year);
      if (isNaN(yearValue) || yearValue <= 0) {
        newErrors.year = 'Год должен быть положительным числом';
      } else if (yearValue > currentYear) {
        newErrors.year = `Год не может быть больше текущего (${currentYear})`;
      }
    }

    // Проверка количества
    if (!formData.quantity) {
      newErrors.quantity = 'Количество обязательно';
    } else if (formData.quantity === '') {
      newErrors.quantity = 'Количество не может быть пустым';
    } else {
      const quantityValue = Number(formData.quantity);
      if (isNaN(quantityValue) || quantityValue < 0) {
        newErrors.quantity = 'Количество должно быть положительным числом';
      }
    }

    if (!formData.isbn.trim()) {
      newErrors.isbn = 'ISBN обязательно';
    } else if (formData.isbn.length > 150) {
      newErrors.isbn = 'ISBN не должно превышать 150 символов';
    }

    if (!formData.annotation.trim()) {
      newErrors.annotation = 'Аннотация обязательна';
    } else if (formData.annotation.length > 1000) {
      newErrors.annotation = 'Аннотация не должна превышать 1000 символов';
    }

    // Проверка файла - делаем изображение необязательным
    if (selectedFile) {
      if (!selectedFile.type.startsWith('image/')) {
        newErrors.image = 'Файл должен быть изображением';
      } else if (selectedFile.size > 5 * 1024 * 1024) {
        newErrors.image = 'Размер изображения не должен превышать 5MB';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Проверка типа файла
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Файл должен быть изображением' }));
        e.target.value = null;
        return;
      }

      // Проверка размера файла
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Размер изображения не должен превышать 5MB' }));
        e.target.value = null;
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setIsLocalPreview(true);
      setErrors(prev => ({ ...prev, image: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Валидация формы перед отправкой
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitFormData = new FormData();
      submitFormData.append('title', formData.title.trim());
      submitFormData.append('quantity', formData.quantity);
      submitFormData.append('author', formData.author.trim());
      submitFormData.append('year', formData.year);
      submitFormData.append('Genr', formData.Genr);
      submitFormData.append('isbn', formData.isbn.trim());
      submitFormData.append('annotation', formData.annotation.trim());

      if (selectedFile) {
        submitFormData.append('image', selectedFile);
      }

      if (editingId) {
        const response = await patchBook({ id: editingId, formData: submitFormData });
        if (response?.book) {
          showNotification('success', "Книга успешно обновлена");
          fetchBooks();
        }
      } else {
        const response = await postNewArrival({ formData: submitFormData });
        if (response?.book) {
          showNotification('success', "Книга успешно создана");
          fetchBooks();
          // При добавлении новой книги переходим на первую страницу
          setCurrentPage(1);
        }
        showNotification("Книга успешно добавлена", "success");
      }

      resetForm();
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      let errorMessage = "Произошла ошибка при сохранении";

      // Обработка возможных ответов сервера с сообщениями об ошибках
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      showNotification('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', quantity: '', author: '', year: '', Genr: '', annotation: '', isbn: '' });
    setSelectedFile(null);
    setPreviewUrl('');
    setIsLocalPreview(false);
    setEditingId(null);
    setErrors({});

    // Очищаем файловое поле
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Очищаем ошибку поля при изменении
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Дополнительные проверки для числовых полей
    if (name === 'quantity' || name === 'year') {
      // Разрешаем только числовые значения и не пустые строки
      if (value === '' || /^\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту книгу?')) {
      setLoading(true);
      try {
        const response = await deleteNewArrival(id);
        // Проверяем успешный ответ более гибко
        if (response) {
          showNotification('success', "Книга успешно удалена");

          // Обновляем список книг после удаления
          const updatedBooks = books.filter(book => book.id !== id);
          setBooks(updatedBooks);

          // Если после удаления на текущей странице не остается книг и это не первая страница,
          // переходим на предыдущую страницу
          const maxPage = Math.ceil(updatedBooks.length / arrivalsPerPage);
          if (currentPage > maxPage && currentPage > 1) {
            setCurrentPage(maxPage);
          }
        }
      } catch (error) {
        console.error('Ошибка при удалении:', error);
        let errorMessage = "Ошибка при удалении книги";

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        showNotification('error', errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (book) => {
    setFormData({
      title: book.title || '',
      author: book.author || '',
      year: book.year || '',
      quantity: book.quantity || '',
      Genr: book.Genr || '',
      isbn: book.isbn || '',
      annotation: book.annotation || '',
    });
    setEditingId(book.id);
    if (book.img_url) {
      setPreviewUrl(`${book.img_url}`);
      setIsLocalPreview(false);
    } else {
      setPreviewUrl('');
    }
    setErrors({});

    // Прокрутка к форме при редактировании
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    // If it's a local preview (from file input), use the URL directly
    if (isLocalPreview) return url;
    // Otherwise, it's from the backend, so add the API URL
    return `${process.env.REACT_APP_API_URL}${url}`;
  };

  // Получаем текущие книги для отображения на странице
  const indexOfLastBook = currentPage * arrivalsPerPage;
  const indexOfFirstBook = indexOfLastBook - arrivalsPerPage;
  const currentBooks = books.slice(indexOfFirstBook, indexOfLastBook);

  // Расчет количества страниц
  const totalPages = Math.ceil(books.length / arrivalsPerPage);

  // Генерация чисел страниц для отображения
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Обработчик нажатия на кнопку страницы
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <MainContainer>
      <Title>{editingId ? 'Редактирование книги' : 'Управление книгами'}</Title>

      <FormContainer onSubmit={handleSubmit}>

        <FormGroup>
          <label>Название книги *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            maxLength={255}
            placeholder="Введите название книги"
            disabled={loading}
          />
          {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Автор *</label>
          <input
            type="text"
            name="author"
            value={formData.author}
            onChange={handleChange}
            maxLength={255}
            placeholder="Введите имя автора"
            disabled={loading}
          />
          {errors.author && <ErrorMessage>{errors.author}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Год издания *</label>
          <input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            min="1"
            max={currentYear}
            placeholder="Введите год издания"
            disabled={loading}
          />
          {errors.year && <ErrorMessage>{errors.year}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Тираж *</label>
          <input
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            min="0"
            placeholder="Введите тираж"
            disabled={loading}
          />
          {errors.quantity && <ErrorMessage>{errors.quantity}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Жанр</label>
          <input
            type="text"
            name="Genr"
            value={formData.Genr}
            onChange={handleChange}
            placeholder="Введите жанр книги"
            disabled={loading}
          />
        </FormGroup>
        <FormGroup>
          <label>ISBN</label>
          <input
            type="text"
            name="isbn"
            value={formData.isbn}
            onChange={handleChange}
            placeholder="Введите ISBN"
            disabled={loading}
          />
          {errors.isbn && <ErrorMessage>{errors.isbn}</ErrorMessage>}
        </FormGroup>
        <FormGroup>
          <label>Аннотация</label>
          <textarea
            type="text"
            name="annotation"
            value={formData.annotation}
            onChange={handleChange}
            placeholder="Введите аннотацию"
            disabled={loading}
          />
          {errors.annotation && <ErrorMessage>{errors.annotation}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <label>Изображение (необязательно)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={loading}
          />
          <FileHelpText>
            Максимальный размер файла: 5 МБ. Допустимые форматы: JPEG, PNG, GIF.
          </FileHelpText>
          {errors.image && <ErrorMessage>{errors.image}</ErrorMessage>}
          {previewUrl && (
            <>
              <PreviewLabel>Предпросмотр</PreviewLabel>
              <PreviewImage src={getImageUrl(previewUrl)} alt="Предпросмотр" />
            </>
          )}
        </FormGroup>

        <ButtonGroup>
          <Button type="submit" disabled={loading}>
            {editingId ? 'Обновить книгу' : 'Добавить книгу'}
            {loading && <LoadingSpinner />}
          </Button>

          {editingId &&
            <Button
              type="button"
              onClick={resetForm}
              disabled={loading}
              secondary
            >
              Отменить редактирование
            </Button>
          }
        </ButtonGroup>
      </FormContainer>

      <BooksSection>
        <SectionTitle>Список книг</SectionTitle>

        {(loading && !books.length) ? (
          <LoadingMessage>
            Загрузка книг... <LoadingIcon />
          </LoadingMessage>
        ) : books.length > 0 ? (
          <>
            <BooksGrid>
              {currentBooks.map(book => (
                <BookCard key={book.id}>
                  {book.img_url && (
                    <BookImageContainer>
                      <BookImage
                        src={`${process.env.REACT_APP_API_URL}${book.img_url}`}
                        alt={book.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/100?text=Нет+фото';
                        }}
                      />
                    </BookImageContainer>
                  )}

                  <BookDetails>
                    <BookTitle>{book.title}</BookTitle>
                    <BookAuthor>
                      {book.author}
                      {book.year && <BookYear> ({book.year})</BookYear>}
                    </BookAuthor>

                    <BookMeta>
                      {book.Genr && <BookGenre>{book.Genr}</BookGenre>}
                      {book.quantity !== undefined && <BookQuantity>{book.quantity} шт.</BookQuantity>}
                    </BookMeta>

                    <ActionButtons>
                      <Button small onClick={() => handleEdit(book)} disabled={loading}>
                        Редактировать
                      </Button>
                      <Button
                        small
                        onClick={() => handleDelete(book.id)}
                        red
                        disabled={loading}
                      >
                        Удалить
                      </Button>
                    </ActionButtons>
                  </BookDetails>
                </BookCard>
              ))}
            </BooksGrid>

            {/* Пагинация */}
            <PaginationContainer>
              <PageButton
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                &laquo;
              </PageButton>

              {pageNumbers.map(number => (
                <PageButton
                  key={number}
                  active={currentPage === number}
                  onClick={() => paginate(number)}
                >
                  {number}
                </PageButton>
              ))}

              <PageButton
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                &raquo;
              </PageButton>
            </PaginationContainer>
          </>
        ) : (
          <EmptyState>
            Книг пока нет. Добавьте первую книгу, используя форму выше.
          </EmptyState>
        )}
      </BooksSection>
    </MainContainer>
  );
}

export default NewArrivals;